'use client';
import { RAISED_SHADOW } from '@/components/room/chrome/selection';

// Login + signup form. Posts to the auth BFF (/api/auth/{login,signup}) with
// credentials:'include' so the jwt cookie the backend sets is stored on this
// origin. On success we navigate to `next` and refresh so server components
// re-render signed-in. The backend already returns friendly `detail` strings
// ("Invalid email or password", "Email already registered", …), so we surface
// those directly and only fall back to status-based copy.
//
// Themed on the editor's --ed-* tokens (resolved by the `guided-theme` scope on
// the login page) so signing in feels like the same room as the app, not a gate
// bolted onto the front.

import { useState, type FormEvent } from 'react';
import Link from 'next/link';
import { ArrowRight, Loader2 } from 'lucide-react';
import { track, identifyUser } from '@/lib/analytics';
import { redditTrack } from '@/lib/redditPixel';
import Button from '@/components/ui/Button';
import { LINK } from '@/components/seo/roles';

type Mode = 'login' | 'signup';

function errorMessage(status: number, detail: unknown, mode: Mode): string {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (status === 429) return 'Too many attempts. Please wait a minute and try again.';
  if (status === 401) return 'Invalid email or password.';
  if (status === 409) return 'That email is already registered — try signing in instead.';
  if (status === 503) return "We couldn't reach the server. Please try again in a moment.";
  return mode === 'signup'
    ? 'Could not create your account. Please try again.'
    : 'Could not sign you in. Please try again.';
}

export default function LoginForm({ next }: { next: string }) {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function switchMode(m: Mode) {
    setMode(m);
    setError(null);
    if (m === 'signup') track('signup_started');
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    track(mode === 'signup' ? 'signup_submitted' : 'login_submitted');
    try {
      const res = await fetch(`/api/auth/${mode}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      if (!res.ok) {
        const detail = await res
          .json()
          .then((d) => (d as { detail?: unknown })?.detail)
          .catch(() => null);
        setError(errorMessage(res.status, detail, mode));
        track(mode === 'signup' ? 'signup_failed' : 'login_failed', { status: res.status });
        setPending(false);
        return;
      }
      // Identify immediately from the response so the anonymous → user merge
      // happens now: this is a soft navigation, so the layout's AnalyticsIdentity
      // won't re-run until a full load. credit_balance / subscription fill in on
      // the next full load via /api/auth/me.
      const data = (await res.json().catch(() => null)) as {
        user?: { id?: number | string; email?: string; email_verified?: boolean; is_premium?: boolean };
      } | null;
      const u = data?.user;
      if (u?.id != null) {
        identifyUser(String(u.id), {
          email: u.email,
          email_verified: !!u.email_verified,
          is_premium: !!u.is_premium,
        });
      }
      track(mode === 'signup' ? 'signup_completed' : 'login_succeeded');
      // Same conversion id the backend's Conversions API SignUp sends, so
      // Reddit counts the pair once (backend/routers/auth.py).
      if (mode === 'signup' && u?.id != null) redditTrack('SignUp', { conversionId: `signup-${u.id}` });
      // HARD navigation (not router.replace + refresh). The `jwt` cookie was
      // just set on this origin by the BFF; a soft SPA navigation into the
      // auth-gated `next` route raced that cookie / the Router Cache and dropped
      // users on Next's "This page couldn't load" error right after signup —
      // they had to reload by hand to recover. A full-page navigation guarantees
      // the cookie rides the request for the gated route and every server
      // component renders fresh, doing that reload automatically. Leave
      // `pending` true — the page tears down on navigation.
      window.location.assign(next || '/rooms');
    } catch {
      setError('Something went wrong. Please check your connection and try again.');
      setPending(false);
    }
  }

  return (
    <div className="w-full max-w-sm">
      <div className="mb-6 text-center">
        <h1 className="text-title font-semibold text-[var(--ed-text)]">
          roomtreatment<span className="text-[var(--ed-accent)]">.diy</span>
        </h1>
        <p className="mt-1.5 text-body-sm text-[var(--ed-text-dim)]">Design your studio.</p>
      </div>

      <div className="rounded-xl border border-[var(--ed-border)] bg-[var(--ed-dock)] p-6 shadow-[0_25px_50px_-12px_color-mix(in_srgb,var(--rt-shadow)_40%,transparent)]">
        <div className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-[var(--ed-surface-2)] p-1">
          {(['login', 'signup'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => switchMode(m)}
              className={`rounded-lg py-1.5 text-body-sm font-semibold transition-colors ${
                mode === m
                  ? `bg-[var(--ed-input)] text-[var(--ed-text)] ${RAISED_SHADOW}`
                  : 'text-[var(--ed-text-dim)] hover:text-[var(--ed-text)]'
              }`}
            >
              {m === 'login' ? 'Sign in' : 'Create account'}
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="space-y-3.5">
          <div>
            <label
              htmlFor="email"
              className="mb-1.5 ed-label block"
            >
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-[var(--ed-border)] bg-[var(--ed-input)] px-3 py-2 text-body text-[var(--ed-text)] outline-none transition-colors placeholder:text-[var(--ed-text-faint)] focus:border-[var(--ed-accent-line)]"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-1.5 ed-label block"
            >
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-[var(--ed-border)] bg-[var(--ed-input)] px-3 py-2 text-body text-[var(--ed-text)] outline-none transition-colors placeholder:text-[var(--ed-text-faint)] focus:border-[var(--ed-accent-line)]"
              placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'}
            />
            {mode === 'login' && (
              <div className="mt-1.5 text-right">
                <Link
                  href="/forgot-password"
                  className="text-caption text-[var(--ed-text-dim)] transition-colors hover:text-[var(--ed-text)]"
                >
                  Forgot password?
                </Link>
              </div>
            )}
          </div>

          {error && (
            <p className="ed-fade-in rounded-lg border border-[var(--ed-red)]/45 bg-[var(--ed-red)]/10 px-3 py-2 text-body-sm leading-snug text-[var(--rt-danger)]">
              {error}
            </p>
          )}

          <Button type="submit" size="md" className="group w-full" disabled={pending}>
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {mode === 'login' ? 'Sign in' : 'Create account'}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>

        {mode === 'signup' && (
          <p className="mt-3 text-caption leading-relaxed text-[var(--ed-text-dim)]">
            We&apos;ll email you a verification link — you can start designing right away.
          </p>
        )}
      </div>

      <p className="mt-4 text-center text-caption text-[var(--ed-text-dim)]">
        {mode === 'login' ? (
          <>
            New here?{' '}
            <button
              type="button"
              onClick={() => switchMode('signup')}
              className={`font-semibold ${LINK}`}
            >
              Create an account
            </button>
          </>
        ) : (
          <>
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => switchMode('login')}
              className={`font-semibold ${LINK}`}
            >
              Sign in
            </button>
          </>
        )}
      </p>
    </div>
  );
}
